
import { useState, useEffect, useCallback } from 'react';
// @ts-ignore
import uploadService from '../../../services/uploadService';

interface LogoFaviconConfig {
  logoUrl: string;
  faviconUrl: string;
}

const LogoAndFaviconManagement = () => {
  const [config, setConfig] = useState<LogoFaviconConfig>({
    logoUrl: '',
    faviconUrl: ''
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  const fetchCurrentFiles = useCallback(async () => {
    try {
      // Fetch both in parallel to avoid multiple calls
      const [logoResponse, faviconResponse] = await Promise.all([
        uploadService.getList({ fileObjectType: 'Logo', limit: 1 }),
        uploadService.getList({ fileObjectType: 'Favicon', limit: 1 })
      ]);
      
      // Access data.items instead of just items
      const logoItems = logoResponse.data?.items || [];
      const faviconItems = faviconResponse.data?.items || [];
      
      if (logoResponse.success && logoItems.length > 0) {
        // Build full URL with MinIO base endpoint
        const logoItem = logoItems[0];
        const logoUrl = logoItem.fullUrl || logoItem.publicUrl;
        
        setConfig(prev => ({
          ...prev,
          logoUrl: logoUrl
        }));
      }

      if (faviconResponse.success && faviconItems.length > 0) {
        // Build full URL with MinIO base endpoint
        const faviconItem = faviconItems[0];
        const faviconUrl = faviconItem.fullUrl || faviconItem.publicUrl;
        
        setConfig(prev => ({
          ...prev,
          faviconUrl: faviconUrl
        }));
      }
    } catch (error) {
      console.error('Error fetching logo/favicon:', error);
    }
  }, []);

  useEffect(() => {
    fetchCurrentFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (type: 'logo' | 'favicon') => {
    if (type === 'logo' && !logoFile) return;
    if (type === 'favicon' && !faviconFile) return;

    setUploading(true);
    try {
      const file = type === 'logo' ? logoFile : faviconFile;
      const objectType = type === 'logo' ? 'Logo' : 'Favicon';
      
      if (!file) return;

      // Upload file với objectType tương ứng
      const result = await uploadService.uploadFile(
        file,
        'site-config', // objectId dùng cho config
        objectType
      );

      if (result.success && result.data) {
        // Fetch lại file mới
        await fetchCurrentFiles();
        
        // Reset file và preview
        if (type === 'logo') {
          setLogoFile(null);
          setLogoPreview('');
        } else {
          setFaviconFile(null);
          setFaviconPreview('');
        }
        
        alert('Upload thành công!');
      } else {
        alert('Upload thất bại: ' + (result.message || 'Lỗi không xác định'));
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      alert('Upload thất bại: ' + (error.message || 'Lỗi không xác định'));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setConfig({ ...config, logoUrl: '' });
  };

  const handleRemoveFavicon = () => {
    setFaviconFile(null);
    setFaviconPreview('');
    setConfig({ ...config, faviconUrl: '' });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Quản lý Logo và Favicon</h2>
      </div>

      {/* Logo Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Logo</h3>
          <p className="text-sm text-gray-600">
            Upload logo của bạn. Logo sẽ được hiển thị ở header của website.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Logo hiện tại
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center min-h-[200px] bg-gray-50">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-h-40 max-w-full object-contain"
                  />
                </div>
              ) : config.logoUrl ? (
                <div className="relative">
                  <img
                    src={config.logoUrl}
                    alt="Current logo"
                    className="max-h-40 max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="text-center text-gray-400">
                  <i className="ri-image-line text-4xl mb-2"></i>
                  <p className="text-sm">Chưa có logo</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Upload logo mới
            </label>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <i className="ri-upload-cloud-line text-3xl text-gray-400 mb-2"></i>
                  <span className="text-sm text-gray-600">
                    Click để chọn hoặc kéo thả file
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    PNG, JPG, SVG (tối đa 2MB)
                  </span>
                </label>
              </div>

              {logoPreview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSubmit('logo')}
                    disabled={uploading}
                    className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Đang upload...
                      </span>
                    ) : (
                      <>
                        <i className="ri-check-line mr-2"></i>
                        Lưu logo
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleRemoveLogo}
                    disabled={uploading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Favicon Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Favicon</h3>
          <p className="text-sm text-gray-600">
            Upload favicon (biểu tượng trang). Favicon sẽ được hiển thị trên tab trình duyệt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Favicon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Favicon hiện tại
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center min-h-[200px] bg-gray-50">
              {faviconPreview ? (
                <div className="relative">
                  <img
                    src={faviconPreview}
                    alt="Favicon preview"
                    className="w-32 h-32 object-contain"
                  />
                </div>
              ) : config.faviconUrl ? (
                <div className="relative">
                  <img
                    src={config.faviconUrl}
                    alt="Current favicon"
                    className="w-32 h-32 object-contain"
                  />
                </div>
              ) : (
                <div className="text-center text-gray-400">
                  <i className="ri-image-line text-4xl mb-2"></i>
                  <p className="text-sm">Chưa có favicon</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Upload favicon mới
            </label>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFaviconChange}
                  className="hidden"
                  id="favicon-upload"
                />
                <label
                  htmlFor="favicon-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <i className="ri-upload-cloud-line text-3xl text-gray-400 mb-2"></i>
                  <span className="text-sm text-gray-600">
                    Click để chọn hoặc kéo thả file
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    ICO, PNG (tối đa 500KB, khuyến nghị 32x32px hoặc 64x64px)
                  </span>
                </label>
              </div>

              {faviconPreview && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSubmit('favicon')}
                    disabled={uploading}
                    className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Đang upload...
                      </span>
                    ) : (
                      <>
                        <i className="ri-check-line mr-2"></i>
                        Lưu favicon
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleRemoveFavicon}
                    disabled={uploading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Xem trước</h3>
        <div className="space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {faviconPreview || config.faviconUrl ? (
                  <img
                    src={faviconPreview || config.faviconUrl}
                    alt="Favicon"
                    className="w-5 h-5"
                  />
                ) : (
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                )}
                <span className="text-sm text-gray-600">Tên tab trình duyệt</span>
              </div>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {logoPreview || config.logoUrl ? (
                  <img
                    src={logoPreview || config.logoUrl}
                    alt="Logo"
                    className="h-10"
                  />
                ) : (
                  <div className="h-10 w-32 bg-gray-200 rounded"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoAndFaviconManagement;
